import { newId } from '@/lib/format'
import type { GuestCircle, Party, Person } from '@/mock/types'

export type ParsedParty = {
  key: string
  line: number
  circle: GuestCircle
  persons: Person[]
  /** "Kolegyně z práce: Simona Benešová a Hana Králová" names the invitation. */
  name?: string
  /** Someone with the same name is already on the list; skipped unless the user keeps it. */
  duplicate: boolean
}

const circleHeadings: Record<string, GuestCircle> = {
  nevesta: 'bride',
  'hoste nevesty': 'bride',
  zenich: 'groom',
  'hoste zenicha': 'groom',
  spolecni: 'shared',
  'spolecni hoste': 'shared',
}

export const fold = (value: string) =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim()

// Czech first names ending in -a/-e are mostly women's ("Jana", "Marie"); good enough for a draft.
const isWoman = (firstName: string) =>
  /[ae]$/i.test(firstName) && !/^(jiri|ilja|sasa|nikola)$/i.test(fold(firstName))

/** "Novákovi" → "Novák" / "Nováková"; "Černí" → "Černý" / "Černá"; a single surname keeps a woman's form. */
function surnameFor(firstName: string, surname: string) {
  const woman = isWoman(firstName)
  if (/ovi$/i.test(surname)) {
    const stem = surname.slice(0, -3)
    return woman ? `${stem}ová` : stem
  }
  if (/[íi]$/.test(surname) && surname.length > 3) {
    const stem = surname.slice(0, -1)
    return woman ? `${stem}á` : `${stem}ý`
  }
  if (!woman) return surname.replace(/ová$/, '').replace(/á$/, 'ý')
  if (/ová$|á$/.test(surname)) return surname
  if (/ý$/.test(surname)) return `${surname.slice(0, -1)}á`
  return `${surname}ová`
}

const person = (firstName: string, lastName: string, extra: Partial<Person> = {}): Person => ({
  id: newId('guest'),
  firstName,
  lastName,
  attendance: 'invited',
  child: false,
  diets: [],
  ...extra,
})

/**
 * One invitation per line. "Jana a Karel Novákovi", "Jana Nováková, Karel
 * Novák", "Ondřej Svoboda +1", "Klára Horáková + doprovod", "Kolegyně z
 * práce: Simona a Hana Králová". A line like "Ženich:" sets whose guests
 * follow; otherwise the default applies.
 */
export function parseGuestList(
  text: string,
  defaultCircle: GuestCircle,
  existing: Party[],
): ParsedParty[] {
  const known = new Set(
    existing.flatMap((party) =>
      party.persons.map((item) => fold(`${item.firstName} ${item.lastName}`)),
    ),
  )
  let circle = defaultCircle
  const parsed: ParsedParty[] = []
  text.split('\n').forEach((raw, index) => {
    const line = raw.replace(/\t+/g, ', ').replace(/\s+/g, ' ').trim()
    if (!line) return
    const heading = circleHeadings[fold(line.replace(/:$/, ''))]
    if (heading && line.endsWith(':')) {
      circle = heading
      return
    }

    const named = line.match(/^([^:]+):\s*(.+)$/)
    const name = named?.[1]?.trim()
    let rest = named?.[2] ?? line
    let companions = 0
    rest = rest.replace(/\s*\+\s*(\d+|doprovod|partner(ka)?)\s*$/i, (_, what: string) => {
      companions = /^\d+$/.test(what) ? Number(what) : 1
      return ''
    })
    const parts = rest
      .split(/\s*[,;&+]\s*|\s+a\s+/i)
      .map((part) => part.trim())
      .filter(Boolean)
    if (parts.length === 0) return

    // The family name usually comes once, at the end: "Jana a Karel Novákovi".
    const lastWords = parts.at(-1)?.split(' ') ?? []
    const sharedSurname = lastWords.length > 1 ? (lastWords.at(-1) ?? '') : ''
    const persons = parts.map((part) => {
      const words = part.split(' ')
      const firstName = words[0] ?? ''
      const own = words.slice(1).join(' ')
      const lastName = own
        ? /ovi$|[^aeiouyáéíóúůý]í$/i.test(own)
          ? surnameFor(firstName, own)
          : own
        : sharedSurname
          ? surnameFor(firstName, sharedSurname)
          : ''
      return person(firstName, lastName)
    })
    for (let i = 0; i < companions; i++) persons.push(person('', '', { plusOne: true }))

    parsed.push({
      key: `${index}-${line}`,
      line: index + 1,
      circle,
      persons,
      ...(name ? { name } : {}),
      duplicate: persons.some(
        (item) => !item.plusOne && known.has(fold(`${item.firstName} ${item.lastName}`)),
      ),
    })
  })
  return parsed
}
